import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { translate } from "../../types/EnumTranslations";
import {
    getCoverPhotoStoragePath,
    getRiskWarningsFromBuilding,
    getAllUniqueHazardsFromBuilding,
    getWorkEnvironmentRisksFromBuilding
} from "../../Helpers/utils";
import ClickableImage from "../../Components/ClickableImage";
import { MapContainer, Marker, TileLayer } from "react-leaflet";
import CategoryTabs from "./CategoryTabs/CategoryTabs";
import { BUILDING_MARKER_COLOR, createSvgMarker, markerSymbolUrl } from "../../MapOverview/CustomIcon";
import { Button, Modal } from "react-bootstrap";
import { PageLoader } from "../../Components/LoadingSpinner";
import { BreadcrumbComponent } from "../../Components/BreadCrumb";
import "./BuildingPage.css"
import BuildingTabs from "./BuildingTabs/BuildingTabs";
import { getToiletComponentTranslation, OfflineLock } from "../../types/protocols";
import { useDocumentTitle } from '../../hooks/useDocumentTitle';
import { useIsSmallScreen } from '../../hooks/useIsSmallScreen';
import { useBuilding } from "../../Helpers/BuildingContext";
import { placeholderForBuildingType } from "../../Helpers/PlaceholderUtils";
import { FirebaseImage, getImagePathWithSize, ImageSize, useFirebaseImage } from "../../Helpers/ImageLoader";
import { Fancybox } from "@fancyapps/ui";
import { BuildingType, iconForBuildingType, iconForGroupTag } from "../../types/BuildingTypeEnums";
import { useBuildComponentTypes } from "../../contexts/BuildComponentTypesContext";
import { useUsersData } from "../../Helpers/UsersContext";
import { getAuth } from "firebase/auth";
import { getDatabase, ref, remove } from "firebase/database";
import { analyticsApi } from "../../api/analyticsApi";
import { recordActivity } from "../../api/activityApi";
import { ArrowUpRight, Edit2, Hammer, Landmark } from 'lucide-react';
import { BuildingInfoEditModal } from "./components/BuildingInfoEditModal";
import { BuildingTypeEditModal } from "./components/BuildingTypeEditModal";
import { BuildingInfo } from "../../types/protocols";
import { buildingInfoApi } from "../../api/buildingInfoApi";
import { buildingTypeApi } from "../../api/buildingTypeApi";

// Format lock timestamp for display (truncate seconds)
const formatLockDate = (lockedAt: string): string => {
    // Input format: "YYYY-MM-DD hh:mm:ss"
    // Output format: "YYYY-MM-DD hh:mm"
    return lockedAt.substring(0, 16);
};

// Offline lock banner component
const OfflineLockBanner: React.FC<{
    lock: OfflineLock;
    holderName: string | null;
    isAdmin: boolean;
    onReleaseLock: () => void;
    isReleasing: boolean;
}> = ({ lock, holderName, isAdmin, onReleaseLock, isReleasing }) => {
    const [showConfirmModal, setShowConfirmModal] = useState(false);

    return (
        <>
            <div style={{
                backgroundColor: '#f57c00',
                color: 'white',
                padding: '12px 16px',
                borderRadius: '8px',
                marginBottom: '20px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
            }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <span style={{ fontSize: '18px' }}>🔒</span>
                    <div>
                        <div style={{ fontWeight: 600, fontSize: '14px' }}>
                            Låst för offline-redigering
                        </div>
                        <div style={{ fontSize: '12px', opacity: 0.9 }}>
                            {holderName ?? 'Annan användare'} · {formatLockDate(lock.lockedAt)}
                        </div>
                    </div>
                </div>
                {isAdmin && (
                    <Button
                        variant="light"
                        size="sm"
                        onClick={() => setShowConfirmModal(true)}
                        disabled={isReleasing}
                        style={{
                            fontSize: '12px',
                            fontWeight: 500,
                            padding: '4px 12px'
                        }}
                    >
                        {isReleasing ? 'Släpper...' : 'Släpp lås'}
                    </Button>
                )}
            </div>

            <Modal show={showConfirmModal} onHide={() => setShowConfirmModal(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Släpp offline-lås</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p>
                        Är du säker på att du vill släppa offline-låset för denna byggnad?
                    </p>
                    <p style={{ color: '#d32f2f', fontWeight: 500 }}>
                        Varning: Om {holderName ?? 'användaren'} har osparade ändringar kan dessa
                        gå förlorade eller skapa konflikter när de försöker synkronisera.
                    </p>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowConfirmModal(false)}>
                        Avbryt
                    </Button>
                    <Button
                        variant="danger"
                        onClick={() => {
                            setShowConfirmModal(false);
                            onReleaseLock();
                        }}
                        disabled={isReleasing}
                    >
                        {isReleasing ? 'Släpper...' : 'Släpp lås'}
                    </Button>
                </Modal.Footer>
            </Modal>
        </>
    );
};

const BuildingPage: React.FC = () => {
    const { id: buildingKey } = useParams<{ id: string }>();
    const { getTranslation } = useBuildComponentTypes();
    const isSmallScreen = useIsSmallScreen();
    const { currentBuilding, buildingError, loadingBuilding, fetchBuilding } = useBuilding();
    const { users } = useUsersData();
    const [isReleasingLock, setIsReleasingLock] = useState(false);
    const trackedBuildingKeyRef = useRef<string | null>(null);
    const [showBuildingInfoModal, setShowBuildingInfoModal] = useState(false);
    const [isSavingBuildingInfo, setIsSavingBuildingInfo] = useState(false);
    const [showBuildingTypeModal, setShowBuildingTypeModal] = useState(false);
    const [isSavingBuildingType, setIsSavingBuildingType] = useState(false);

    // Get current user and check admin status
    const auth = getAuth();
    const currentUserId = auth.currentUser?.uid;
    const currentUser = users?.find(u => u.key === currentUserId);
    const isAdmin = currentUser?.admin ?? false;
    const isSuperadmin = currentUser?.superadmin ?? false;

    // Check if building is locked by another user
    const offlineLock = currentBuilding?.summary.offlineLock;
    const isLockedByOther = offlineLock && offlineLock.lockedBy !== currentUserId;

    // Get lock holder's name
    const lockHolderName = offlineLock
        ? users?.find(u => u.key === offlineLock.lockedBy)?.name ?? null
        : null;

    // Release lock function (admin only)
    const handleReleaseLock = useCallback(async () => {
        if (!buildingKey || !isAdmin) return;

        setIsReleasingLock(true);
        try {
            const db = getDatabase();
            const lockRef = ref(db, `buildingsSummary/${buildingKey}/offlineLock`);
            await remove(lockRef);
            recordActivity('building.updated', { buildingKey, targetKey: buildingKey });
            // Refresh building data to update UI
            await fetchBuilding(buildingKey);
        } catch (error) {
            console.error('Failed to release lock:', error);
        } finally {
            setIsReleasingLock(false);
        }
    }, [buildingKey, isAdmin, fetchBuilding]);

    // Save building name and info (admin only)
    const handleSaveBuildingInfo = useCallback(async (name: string, updatedInfo: BuildingInfo) => {
        if (!buildingKey) return;

        setIsSavingBuildingInfo(true);
        try {
            await buildingInfoApi.updateBuildingInfo(buildingKey, name, updatedInfo);
            await fetchBuilding(buildingKey);
        } finally {
            setIsSavingBuildingInfo(false);
        }
    }, [buildingKey, fetchBuilding]);

    // Save building type (superadmin only)
    const handleSaveBuildingType = useCallback(async (buildingType: BuildingType) => {
        if (!buildingKey) return;

        setIsSavingBuildingType(true);
        try {
            await buildingTypeApi.updateBuildingType(buildingKey, buildingType);
            await fetchBuilding(buildingKey);
        } finally {
            setIsSavingBuildingType(false);
        }
    }, [buildingKey, fetchBuilding]);

    // Set document title based on state
    useDocumentTitle(
        buildingError ? 'Fel - Molnfastigheter' :
        loadingBuilding ? 'Laddar...' :
        !currentBuilding ? 'Byggnad saknas' :
        currentBuilding.summary.name ? `${currentBuilding.summary.name} - Molnfastigheter` :
        'Byggnad - Molnfastigheter'
    );

    const coverImage = currentBuilding ? getImagePathWithSize(getCoverPhotoStoragePath(currentBuilding.summary), ImageSize.FULL) : null;
    const { src, isLoading, hasError } = useFirebaseImage(coverImage, 'placeholder');

    useEffect(() => {
        const loadBuildingData = async () => {
            if (buildingKey) {
                await fetchBuilding(buildingKey);
            }
        };

        loadBuildingData().then();
    }, [buildingKey, fetchBuilding]);

    // Track building view when building loads successfully (once per buildingKey)
    useEffect(() => {
        if (currentBuilding && buildingKey && trackedBuildingKeyRef.current !== buildingKey) {
            trackedBuildingKeyRef.current = buildingKey;
            analyticsApi.trackBuildingViewed(buildingKey);
        }
    }, [currentBuilding, buildingKey]);

    // The mini map's marker is the overview map's, without the name label (the title already has it)
    const summaryForMarker = currentBuilding?.summary;
    const mapMarkerIcon = useMemo(
        () => summaryForMarker ? createSvgMarker(BUILDING_MARKER_COLOR, markerSymbolUrl(summaryForMarker)) : undefined,
        [summaryForMarker]
    );

    // Calculate active todo count from building model
    const activeTodoCount = currentBuilding?.model.todos 
        ? Object.values(currentBuilding.model.todos).filter(todo => !todo.done).length 
        : 0;

    if (buildingError) {
        return (
            <div style={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    minHeight: '50vh',
                    padding: '20px'
                }}>
                    <div className="std-box" style={{
                        padding: '40px',
                        textAlign: 'center',
                        maxWidth: '400px',
                        width: '100%'
                    }}>
                        <h3 style={{
                            color: '#d32f2f',
                            fontSize: '18px',
                            fontWeight: '600',
                            marginBottom: '16px'
                        }}>Fel uppstod</h3>
                        <p style={{
                            color: '#666',
                            fontSize: '14px',
                            lineHeight: '1.5',
                            margin: 0
                        }}>{buildingError}</p>
                    </div>
                </div>
        );
    }

    if (loadingBuilding) {
        return <PageLoader />;
    }

    if (!currentBuilding) {
        return <div>Byggnad saknas</div>;
    }

    const { model, summary } = currentBuilding;

    const riskConstructions = getRiskWarningsFromBuilding(currentBuilding)
    const hazards = getAllUniqueHazardsFromBuilding(currentBuilding)
    const workEnvironmentRisks = getWorkEnvironmentRisksFromBuilding(currentBuilding)

    const toiletKey = (summary.buildingType === BuildingType.toiletReserve || summary.buildingType === BuildingType.toiletArchipelago)
        ? getToiletComponentTranslation(summary, model)
        : null;
    const typeLabel = (
        <>
            <img src={iconForBuildingType(summary.buildingType)} alt="" />
            {translate(summary.buildingType)}
            {toiletKey ? ` – ${getTranslation(toiletKey)}` : ''}
        </>
    );

    return (
        <div className="building-page">
            <BreadcrumbComponent
                items={[
                    { text: summary.name ?? 'Byggnad' },
                ]}
            />

            {/* Layout classes live in BuildingPage.css, with the phone layout in its media query. */}
            <div className="building-page-content">
                {/* Offline lock banner - show when building is locked by another user */}
                {isLockedByOther && offlineLock && (
                    <OfflineLockBanner
                        lock={offlineLock}
                        holderName={lockHolderName}
                        isAdmin={isAdmin}
                        onReleaseLock={handleReleaseLock}
                        isReleasing={isReleasingLock}
                    />
                )}
                <div className="building-page-top">
                    {/* Left Column - Cover Photo + Main Content */}
                    <div className="building-page-main">
                        {/* Cover Photo */}
                        <div className="cover-photo-container">
                            <div className="cover-photo-wrapper">
                                <FirebaseImage
                                    storagePath={getCoverPhotoStoragePath(summary)}
                                    size={ImageSize.FULL}
                                    placeholder={placeholderForBuildingType(currentBuilding.summary.buildingType)}
                                    alt="Background"
                                    style={{
                                        width: '100%',
                                        height: '100%',
                                        objectFit: 'cover',
                                        cursor: coverImage ? 'pointer' : 'default'
                                    }}
                                    onClick={() => {
                                        if (coverImage) {
                                            Fancybox.show(
                                                [
                                                    {
                                                        src: src,
                                                        caption: "Omslagsbild",
                                                    }
                                                ],
                                                {
                                                    animated: true,
                                                    showClass: "fancybox-fadeIn",
                                                }
                                            );
                                        }
                                    }}
                                />
                                <img
                                    src="/Images/UI/magnifier.png"
                                    alt="Magnifier"
                                    className="cover-photo-magnifier"
                                    onClick={() => {
                                        if (coverImage) {
                                            Fancybox.show(
                                                [
                                                    {
                                                        src: src,
                                                        caption: "Omslagsbild",
                                                    }
                                                ],
                                                {
                                                    animated: true,
                                                    showClass: "fancybox-fadeIn",
                                                }
                                            );
                                        }
                                    }}
                                />
                            </div>
                        </div>

                        {/* Main Content */}
                        <div className="dropshadow building-page-card-body">

                    <div className="building-page-title-row">
                        {/* The name wraps in its own box, so the edit button stays in the top right corner */}
                        <h1 className="building-page-name page-title">{summary.name}</h1>

                        {/* Admin: the name and all the building-info fields, spread over the title, chips, aside and Allmänt */}
                        {(isAdmin || isSuperadmin) && (
                            <button
                                type="button"
                                className="building-page-icon-button building-page-info-edit"
                                onClick={() => setShowBuildingInfoModal(true)}
                                title="Redigera fastighetsuppgifter"
                            >
                                <Edit2 size={18} />
                            </button>
                        )}
                    </div>
                    {/* The building type first: there is always at least that one tag */}
                    <div className="building-page-chips">
                        {/* Superadmins change the type from the tag itself. */}
                        {isSuperadmin ? (
                            <button
                                type="button"
                                className="building-page-chip building-page-chip--editable"
                                title="Ändra byggnadstyp"
                                onClick={() => setShowBuildingTypeModal(true)}
                            >
                                {typeLabel}
                            </button>
                        ) : (
                            <span className="building-page-chip">{typeLabel}</span>
                        )}
                        {summary.buildingInfo?.yearBuilt && <span className="building-page-chip"><Hammer size={12} aria-hidden="true" />Byggnadsår: {summary.buildingInfo.yearBuilt}</span>}
                        {summary.buildingInfo?.heritageProtectionLevel?.map(level => (
                            <span key={level} className="building-page-chip building-page-chip--heritage" title="Kulturvärdesnivå: beskriver byggnaders kulturhistoriska skyddsnivåer i syfte att stödja planering, tillståndsprövning och underhåll med hänsyn till bevarandevärden.">
                                <Landmark size={12} aria-hidden="true" />
                                {translate(level)}
                            </span>
                        ))}
                        {/* Group tags as page chips rather than BuildingGroupTags, so all chips here share one look */}
                        {Object.values(summary.groupTags ?? {}).map((tag, index) => (
                            <span key={`${tag.key ?? tag.tag}-${index}`} className="building-page-chip">
                                {iconForGroupTag(tag.tag) && <img src={iconForGroupTag(tag.tag)} alt="" />}
                                {tag.tag}
                            </span>
                        ))}
                    </div>
                    <CategoryTabs
                        building={currentBuilding}
                        buildingKey={buildingKey ?? ''}
                        isAdmin={isAdmin}
                        todoCount={activeTodoCount}
                        risks={{ hazards: hazards.length, riskConstructions: riskConstructions.length, workEnvironment: workEnvironmentRisks.length }}
                    />
                        </div>
                    </div>

                    {/* Building Info Edit Modal */}
                    <BuildingInfoEditModal
                        show={showBuildingInfoModal}
                        onHide={() => setShowBuildingInfoModal(false)}
                        onSave={handleSaveBuildingInfo}
                        name={summary.name}
                        buildingInfo={summary.buildingInfo}
                        submitting={isSavingBuildingInfo}
                    />

                    {/* Building Type Edit Modal (superadmin only) */}
                    <BuildingTypeEditModal
                        show={showBuildingTypeModal}
                        onHide={() => setShowBuildingTypeModal(false)}
                        onSave={handleSaveBuildingType}
                        buildingType={summary.buildingType}
                        submitting={isSavingBuildingType}
                    />

                    {/* Right Column - Map and Compass (325px on desktop, full width on phones) */}
                    <div className="building-page-aside">
                        <div className="std-box building-page-location">
                            {/* Mini Map: not on phones, where the map isn't usable yet */}
                            {!isSmallScreen && summary.coordinates?.latitude && summary.coordinates?.longitude && (
                                <div className="building-page-map">
                                    <MapContainer
                                        key={`map-${summary.coordinates.latitude}-${summary.coordinates.longitude}`}
                                        center={[summary.coordinates.latitude, summary.coordinates.longitude]}
                                        zoom={13}
                                        style={{ height: '100%', width: '100%' }}
                                    >
                                        <TileLayer
                                            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                                            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                                        />
                                        <Marker position={[summary.coordinates.latitude, summary.coordinates.longitude]} {...(mapMarkerIcon ? { icon: mapMarkerIcon } : {})} />
                                    </MapContainer>
                                </div>
                            )}

                            <dl className="building-page-facts">
                                <div>
                                    <dt>Adress</dt>
                                    <dd>
                                        {(summary.buildingInfo?.street || summary.buildingInfo?.postalCode) ? (
                                            <>
                                                {summary.buildingInfo?.street ?? "[Gatuadress]"}
                                                <br />
                                                {`${summary.buildingInfo?.postalCode ?? "[Postnummer]"} ${summary.buildingInfo?.city ?? "[Postort]"}`}
                                            </>
                                        ) : "-"}
                                    </dd>
                                </div>
                                <div>
                                    <dt>Kommun</dt>
                                    <dd>{summary.buildingInfo?.municipality || "-"}</dd>
                                </div>
                                {summary.coordinates?.latitude && summary.coordinates?.longitude && (
                                    <div>
                                        <dt>Koordinater</dt>
                                        <dd>{`${summary.coordinates.latitude}, ${summary.coordinates.longitude}`}</dd>
                                    </div>
                                )}
                            </dl>

                            {summary.coordinates?.latitude && summary.coordinates?.longitude && (
                                <a
                                    className="building-page-maps-link"
                                    href={`https://maps.google.com/?q=${summary.coordinates.latitude},${summary.coordinates.longitude}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    Google Maps
                                    <ArrowUpRight size={14} aria-hidden="true" />
                                </a>
                            )}

                            {/* Satellite Image with Compass */}
                            {model.currentMapImageKey && model.mapImages && (
                                <div className="building-page-compass">
                                    <ClickableImage
                                        buildingKey={model.key}
                                        photo={model.mapImages[model.currentMapImageKey]}
                                        size={285}
                                        fill
                                        showDate={false}
                                    />
                                    <img src="/Images/UI/compass.png" alt="" className="building-page-compass-rose" />
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {buildingKey && <BuildingTabs building={currentBuilding} buildingKey={buildingKey} />}
            </div>
        </div>


    );
};

export default BuildingPage;
